import React from 'react';
import { css } from '@emotion/react';
import { color } from '@/styles/color';
import diaperSvg from '@/components/svg/diaper.svg';
import toiletSvg from '@/components/svg/toilet.svg';
import { t } from 'i18next';
import { getPrintPx } from './printChartOptions';

interface ExportTimeRangeDisplayProps {
    detectUrinationHeatmap: DetectUrinationHeatmapProps;
    isPrinting?: boolean;
}

interface DetectUrinationHeatmapProps {
    data: Record<string, boolean[]> | null;
    xCategories: string[];
    yCategories: string[];
}

interface InternalTimeRangeDisplayProps {
    categories: string[];
    filterCondition: Record<string, boolean[]>;
    isPrinting: boolean;
}

interface TimeRangeDisplayProps {
    icon: typeof diaperSvg;
    highlightText: string;
    highlightColor: string;
    categories: string[];
    filterCondition: boolean[];
    isPrinting: boolean;
}

const flexColumnContainer = (isPrinting: boolean) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding-left: ${getPrintPx(24, isPrinting)}px;
    padding-bottom: ${getPrintPx(24, isPrinting)}px;
`;

const CardContainer = (isPrinting: boolean) => css`
    display: flex;
    flex-direction: column;
    padding: ${getPrintPx(16, isPrinting)}px;
    background-color: ${color.light_gray};
    border-radius: 8px;
    width: fit-content;
    border: 1px solid ${color.border_gray};
  `;

const titleContainer = (isPrinting: boolean) => css`
    display: flex;
    align-items: center;
    font-weight: bold;
    font-size: ${getPrintPx(20, isPrinting)}px;
    &:not(:first-of-type) {
      margin-top: ${getPrintPx(8, isPrinting)}px;
    }
  `;

const titleText = css`
margin: 0;
padding: 0;
white-space: pre-wrap;
`;

const iconStyle = (isPrinting: boolean) => css`
  width: ${getPrintPx(20, isPrinting)}px;
  height: ${getPrintPx(20, isPrinting)}px;
  margin-right: ${getPrintPx(4, isPrinting)}px;
  vertical-align: middle;
`;


const highlightedText = (highlightColor: string) => css`
    color: ${highlightColor};
  `;

const TimeRangeDisplays = React.memo(({ detectUrinationHeatmap, isPrinting = false }: ExportTimeRangeDisplayProps) => {
    const categories = detectUrinationHeatmap.xCategories;
    const filterCondition = detectUrinationHeatmap.data;

    if (!categories || !filterCondition) {
        return null;
    }

    return (
        <div css={flexColumnContainer(isPrinting)}>
            <div css={CardContainer(isPrinting)}>
                <PoolUrineTimeRangeDisplay categories={categories} filterCondition={filterCondition} isPrinting={isPrinting} />
                <UrinationTimeRangeDisplay categories={categories} filterCondition={filterCondition} isPrinting={isPrinting} />
            </div>
        </div>
    );
});

export default TimeRangeDisplays;

function PoolUrineTimeRangeDisplay({ categories, filterCondition, isPrinting }: InternalTimeRangeDisplayProps) {
    return TimeRangeDisplay({
        icon: toiletSvg,
        highlightText: t("estimatedToiletGuidance", "トイレ誘導の目安"),
        highlightColor: "#ffc000",
        categories: categories,
        filterCondition: filterCondition.poolUrine,
        isPrinting,
    });
}

function UrinationTimeRangeDisplay({ categories, filterCondition, isPrinting }: InternalTimeRangeDisplayProps) {
    return TimeRangeDisplay({
        icon: diaperSvg,
        highlightText: t("estimatedDiaperUse", "おむつ交換の目安"),
        highlightColor: "#fab1bfff",
        categories: categories,
        filterCondition: filterCondition.urination,
        isPrinting,
    });
}

function TimeRangeDisplay({
                              icon,
                              highlightText,
                              highlightColor,
                              categories,
                              filterCondition,
                              isPrinting,
                          }: TimeRangeDisplayProps) {

    const getHighlightedCategories = (categories: string[], filterCondition: boolean[]) => {

        let startIndex: number | null = null;
        let filteredCategories: string[] = [];

        [...filterCondition, false].forEach((condition, i) => {
            if (condition) {
                if (startIndex === null) {
                    startIndex = i;
                }
            } else if (startIndex !== null) {
                const continueCount = i - startIndex;
                if (continueCount >= 2) {
                    filteredCategories.push(`${categories[startIndex]}-/${categories[startIndex + continueCount - 1]}`);
                } else {
                    filteredCategories.push(categories[startIndex]);
                }
                startIndex = null;
            }
        });

        return filteredCategories;
    }

    const renderCategories = (categories: string[]) => {
        return categories
            .map((category, index, array) => (
                <span key={index}>
                    {category}
                    {index < array.length - 1 ? '-,\u00A0\u00A0' : '-'}
                </span>
            ));
    };

    const highlightedCategories = getHighlightedCategories(categories, filterCondition);

    return (
        <div css={titleContainer(isPrinting)}>
            <p css={titleText}>
                <img
                    src={icon}
                    alt={highlightText}
                    css={iconStyle(isPrinting)}
                />
                <span css={highlightedText(highlightColor)}>
                    {highlightText}
                </span>
                {'： '}
            </p>
            {highlightedCategories.length > 0 ? (
                renderCategories(highlightedCategories)
            ) : (
                <span>-</span>
            )}
        </div>
    );
}
